import React, { useEffect, useState } from 'react';
import { Alert, Modal, notification, Popconfirm, Button, Avatar, Card, Table, Select, Tag, Grid, Spin, Empty, Pagination } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { FaEdit } from 'react-icons/fa';
import drEasyApi from '../../../Api/Customapi';
import ImageUploader from '../../Ui/ImageUploader';

const { useBreakpoint } = Grid;

const REPORT_TYPES = [
    { value: 0, label: 'General' },
    { value: 1, label: 'Physical Therapy' },
    { value: 2, label: 'Lymphoedema' },
];

const reportTypeColors = {
    0: 'blue',
    1: 'green',
    2: 'purple',
};

export default function Specializations() {
    const screens = useBreakpoint();
    const isMobile = screens.md === false; // < 768px

    const [current, setCurrent] = useState(1);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [specialties, setSpecialties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpenForEdit, setIsModalOpenForEdit] = useState(false);
    const [id, setSpecialtyId] = useState(null);
    const [isModalOpenForAdd, setIsModalOpenForAdd] = useState(false);
    const [specialtyName, setSpecialtyName] = useState('');
    const [specialtyNameAr, setSpecialtyNameAr] = useState('');
    const [reportType, setReportType] = useState(0);
    const [errorName, setErrorName] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [hovered, setHovered] = useState(false);
    const [imgID, setImgID] = useState(null);

    useEffect(() => {
        getSpecialties();
    }, [current]);

    async function getSpecialties() {
        setLoading(true);
        try {
            const response = await drEasyApi.get('/api/Specialization/GetAllSpecializations');
            setSpecialties(response.data);
        } catch (error) {
            console.error("Error fetching specializations:", error);
        } finally {
            setLoading(false);
        }
    }

    const handleImageChange = async (e) => {
        setUploading(true);
        const file = e.target.files[0];
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        const formData = new FormData();

        if (file && allowedTypes.includes(file.type)) {
            formData.append('File', file);
            try {
                const response = await drEasyApi.post('/api/Storage/UploadImage', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setImgID(response?.data?.id);
            } catch (error) {
                notification.error({ message: 'Upload Failed', description: 'Failed to upload image. Please try again.' });
            } finally {
                setUploading(false);
            }
        } else {
            notification.warning({ message: 'Invalid File', description: 'Please select a valid image file (JPEG/JPG/PNG)' });
            setUploading(false);
        }
    };

    async function addSpecialty(nameEn, nameAr) {
        setSending(true);
        try {
            await drEasyApi.post('/api/Specialization/AddSpecialization', {
                nameEn,
                nameAr,
                photoId: imgID,
                reportType,
            });
            notification.success({ message: 'Added Successfully', description: 'Specialty added successfully.' });
            setIsModalOpenForAdd(false);
            resetForm();
            getSpecialties();
        } catch (error) {
            if (error.response?.status === 400) {
                setErrorName('This specialty name already exists');
            } else {
                notification.error({ message: 'Error', description: 'Failed to add specialty. Please try again.' });
            }
        } finally {
            setSending(false);
        }
    }

    async function editSpecialty(nameEn, nameAr) {
        setSending(true);
        try {
            await drEasyApi.put('/api/Specialization/EditSpecialization', {
                id,
                nameEn,
                nameAr,
                photoId: imgID,
                reportType,
            });
            notification.success({ message: 'Updated Successfully', description: 'Specialty updated successfully.' });
            setIsModalOpenForEdit(false);
            resetForm();
            getSpecialties();
        } catch (error) {
            if (error.response?.status === 400) {
                setErrorName('This specialty name already exists');
            } else {
                notification.error({ message: 'Error', description: 'Failed to update specialty. Please try again.' });
            }
        } finally {
            setSending(false);
        }
    }

    async function deleteSpecialty(id) {
        try {
            await drEasyApi.delete(`/api/Specialization/DeleteSpecialization?id=${id}`);
            notification.success({ message: 'Deleted Successfully', description: 'Specialty deleted successfully.' });
            getSpecialties();
        } catch (error) {
            const msg = error.response?.data?.message || 'Failed to delete specialty';
            setErrorMessage(msg);
            notification.error({ message: 'Error', description: msg });
        }
    }

    const showEditModal = (record) => {
        setSpecialtyId(record.id);
        setSpecialtyName(record.nameEn);
        setSpecialtyNameAr(record.nameAr);
        setImgID(record.photoId);
        setReportType(record.reportType ?? 0);
        setIsModalOpenForEdit(true);
    };

    const handleEditOk = () => {
        const trimmedName = specialtyName.trim();
        const trimmedNameAr = specialtyNameAr.trim();
        if (!validateSpecialtyName(trimmedName)) return;
        if (!validateSpecialtyName(trimmedNameAr)) return;
        editSpecialty(trimmedName, trimmedNameAr);
    };

    const handleAddOk = () => {
        const trimmedName = specialtyName.trim();
        const trimmedNameAr = specialtyNameAr.trim();
        if (!validateSpecialtyName(trimmedName)) return;
        if (!validateSpecialtyName(trimmedNameAr)) return;
        if (!imgID) { setErrorName("Please upload an image for the specialty"); return; }
        addSpecialty(trimmedName, trimmedNameAr);
    };

    function validateSpecialtyName(name) {
        if (!name) { setErrorName("Please enter a name for the specialty"); return false; }
        if (name.charAt(0) === ' ') { setErrorName("First character should not be a space"); return false; }
        if (/\s\s/.test(name)) { setErrorName("Consecutive spaces are not allowed"); return false; }
        if (name.length < 3 || name.length > 50) { setErrorName("Specialty name should be between 3 to 50 characters"); return false; }
        setErrorName(null);
        return true;
    }

    const resetForm = () => {
        setSpecialtyName('');
        setSpecialtyNameAr('');
        setReportType(0);
        setImgID(null);
        setErrorName(null);
    };

    const handleCancel = () => {
        resetForm();
        setIsModalOpenForEdit(false);
        setIsModalOpenForAdd(false);
    };

    const removeImg = () => setImgID(null);

    // Shared form body used in both modals.
    // (Called as a function, not as <Component />, so inputs/Select don't remount on every keystroke.)
    const renderFormBody = () => (
        <>
            <div className="mb-4">
                <label className="block font-semibold mb-2">Specialty Name (English)</label>
                <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-base"
                    value={specialtyName}
                    onChange={(e) => setSpecialtyName(e.target.value)}
                    placeholder="Enter specialty name in English"
                />
            </div>
            <div className="mb-4">
                <label className="block font-semibold mb-2">Specialty Name (Arabic)</label>
                <input
                    type="text"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-right text-base"
                    dir="rtl"
                    value={specialtyNameAr}
                    onChange={(e) => setSpecialtyNameAr(e.target.value)}
                    placeholder="أدخل اسم التخصص بالعربية"
                />
            </div>
            <div className="mb-4">
                <label className="block font-semibold mb-2">Report Type</label>
                <Select
                    className="w-full"
                    value={reportType}
                    onChange={setReportType}
                    options={REPORT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                />
            </div>
            <ImageUploader
                spec={true}
                value={imgID}
                onChange={(id) => setImgID(id)}
            />
            {errorName && <Alert message={errorName} type="error" className="mt-2" />}
        </>
    );

    const modalCommonProps = {
        centered: true,
        width: "min(520px, calc(100vw - 24px))",
        styles: { body: { maxHeight: "70vh", overflowY: "auto", overflowX: "hidden" } },
    };

    const columns = [
        {
            title: "Specialty (EN)",
            dataIndex: "nameEn",
            key: "nameEn",
            width: isMobile ? 190 : undefined,
            fixed: isMobile ? "left" : undefined,
            render: (nameEn, record) => (
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <Avatar
                        shape="circle"
                        size={isMobile ? 36 : 48}
                        className="shrink-0"
                        src={`${process.env.REACT_APP_BASE_URL}/api/Storage/GetImageById?id=${record.photoId}`}
                    />
                    <span className="font-semibold text-primary text-sm sm:text-base break-words leading-tight">
                        {nameEn}
                    </span>
                </div>
            ),
        },
        {
            title: "Specialty (AR)",
            dataIndex: "nameAr",
            key: "nameAr",
            width: isMobile ? 150 : undefined,
            render: (nameAr) => (
                <span className="font-medium text-sm sm:text-base" dir="rtl">{nameAr || '-'}</span>
            ),
        },
        {
            title: "Report Type",
            dataIndex: "reportType",
            key: "reportType",
            width: isMobile ? 140 : 160,
            render: (type) => {
                const match = REPORT_TYPES.find((t) => t.value === type);
                return (
                    <Tag color={reportTypeColors[type] ?? 'default'} className="!m-0">
                        {match?.label ?? 'Unknown'}
                    </Tag>
                );
            },
        },
        {
            title: "Actions",
            key: "actions",
            width: isMobile ? 90 : 120,
            fixed: isMobile ? "right" : undefined,
            render: (_, record) => (
                <div className="flex gap-1 sm:gap-2">
                    <Button
                        icon={<EditOutlined />}
                        type="text"
                        className="text-primary hover:text-primary_hover"
                        onClick={() => showEditModal(record)}
                    />
                    <Popconfirm
                        title="Delete Specialty"
                        placement={isMobile ? 'topRight' : 'bottomLeft'}
                        overlayStyle={{ maxWidth: 'calc(100vw - 24px)' }}
                        description={
                            <>
                                <p>Are you sure you want to delete this specialty?</p>
                                <p className="text-red-600 font-semibold">This will also delete all associated doctors and cannot be undone.</p>
                            </>
                        }
                        onConfirm={() => deleteSpecialty(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button danger icon={<DeleteOutlined />} type="text" />
                    </Popconfirm>
                </div>
            ),
        },
    ];

    // ── Mobile: card list instead of table ──
    const PAGE_SIZE = 10;
    const pagedSpecialties = specialties.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

    const renderMobileCards = () => (
        <Spin spinning={loading}>
            {!loading && specialties.length === 0 ? (
                <Empty className="py-10" />
            ) : (
                <div className="flex flex-col gap-3 min-h-[120px]">
                    {pagedSpecialties.map((record) => {
                        const match = REPORT_TYPES.find((t) => t.value === record.reportType);
                        return (
                            <div
                                key={record.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                            >
                                <div className="h-1 bg-gradient-to-r from-primary to-primary/50" />
                                <div className="p-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        <Avatar
                                            shape="circle"
                                            size={56}
                                            className="shrink-0"
                                            src={`${process.env.REACT_APP_BASE_URL}/api/Storage/GetImageById?id=${record.photoId}`}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="font-bold text-primary text-base leading-tight break-words">
                                                {record.nameEn}
                                            </p>
                                            <p className="text-gray-600 text-sm mt-1 break-words" dir="rtl" style={{ textAlign: 'right' }}>
                                                {record.nameAr || '-'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                                        <Tag color={reportTypeColors[record.reportType] ?? 'default'} className="!m-0">
                                            {match?.label ?? 'Unknown'}
                                        </Tag>
                                        <div className="flex gap-2">
                                            <Button
                                                icon={<EditOutlined />}
                                                className="text-primary border-primary"
                                                onClick={() => showEditModal(record)}
                                            >
                                                Edit
                                            </Button>
                                            <Popconfirm
                                                title="Delete Specialty"
                                                placement="topRight"
                                                overlayStyle={{ maxWidth: 'calc(100vw - 24px)' }}
                                                description={
                                                    <>
                                                        <p>Are you sure you want to delete this specialty?</p>
                                                        <p className="text-red-600 font-semibold">This will also delete all associated doctors and cannot be undone.</p>
                                                    </>
                                                }
                                                onConfirm={() => deleteSpecialty(record.id)}
                                                okText="Yes"
                                                cancelText="No"
                                            >
                                                <Button danger icon={<DeleteOutlined />} />
                                            </Popconfirm>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {specialties.length > PAGE_SIZE && (
                <div className="flex justify-center mt-4">
                    <Pagination
                        simple
                        current={current}
                        pageSize={PAGE_SIZE}
                        total={specialties.length}
                        showSizeChanger={false}
                        onChange={(page) => {
                            setCurrent(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    />
                </div>
            )}
        </Spin>
    );

    return (
        <>
            <div className="p-3 sm:p-4 md:p-6 bg-[#F7FCFD] min-h-screen overflow-x-hidden">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                    <h1 className="text-xl md:text-2xl font-bold text-primary">Specialties</h1>
                    <Button
                        icon={<PlusOutlined />}
                        onClick={() => setIsModalOpenForAdd(true)}
                        size={isMobile ? "middle" : "large"}
                        block={isMobile}
                        className="bg-gradient-to-r text-white from-primary to-primary_hover md:hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-xl font-semibold transition-all"
                    >
                        Add Specialty
                    </Button>
                </div>

                {errorMessage && (
                    <div className="mb-4">
                        <Alert message={errorMessage} type="error" closable onClose={() => setErrorMessage(null)} />
                    </div>
                )}

                {isMobile ? (
                    renderMobileCards()
                ) : (
                    <Card className="shadow-sm">
                        <Table
                            columns={columns}
                            dataSource={specialties}
                            loading={loading}
                            rowKey="id"
                            bordered
                            rowClassName={(_, index) => index % 2 === 0 ? 'bg-gray-50' : ''}
                            pagination={{
                                current,
                                onChange: setCurrent,
                                pageSize: 10,
                                showTotal: (total) => `Total ${total} specialties`,
                                showSizeChanger: true,
                                pageSizeOptions: ["10", "20", "50"],
                            }}
                        />
                    </Card>
                )}
            </div>

            {/* Edit Modal */}
            <Modal
                {...modalCommonProps}
                title={<span className="flex items-center"><FaEdit className="mr-2" /> Edit Specialty</span>}
                open={isModalOpenForEdit}
                onOk={handleEditOk}
                onCancel={handleCancel}
                confirmLoading={sending}
                okText="Save"
            >
                {renderFormBody()}
            </Modal>

            {/* Add Modal */}
            <Modal
                {...modalCommonProps}
                title="Add Specialty"
                open={isModalOpenForAdd}
                onOk={handleAddOk}
                onCancel={handleCancel}
                confirmLoading={sending}
                okText="Add"
                okButtonProps={{ style: { backgroundColor: '#298d9f', borderColor: '#298d9f' } }}
            >
                {renderFormBody()}
            </Modal>
        </>
    );
}